import React, { useState, useRef, useEffect } from 'react';
import { pathwaysData } from '../data/pathwaysData';
import PathwayBlock from './PathwayBlock';
import './PathwaysSection.css';

function PathwaysSection({ handleEnrollNow }) {
    const [expandedId, setExpandedId] = useState(null);

    const pathwayRefs = useRef({});

    const handleToggleExpand = (id) => {
        setExpandedId(prevId => (prevId === id ? null : id));
    };

    const handleEnrollNowClick = (product) => {
        if (handleEnrollNow) {
            handleEnrollNow(product);
        }
    };

    useEffect(() => {
        if (expandedId !== null) {
            const timer = setTimeout(() => {
                const element = pathwayRefs.current[expandedId];
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [expandedId]);

    return (
        <section id="pathways-section" className="pathways-section">
            <h2>Professional Development Pathways</h2>
            <p className="section-intro">Follow a curated journey from foundational skills to advanced leadership. Each pathway is a comprehensive program designed for deep, sustained professional growth.</p>

            {pathwaysData.map(pathway => {
                const isExpanded = expandedId === pathway.id;

                return (
                    <div key={pathway.id} className={`pathway-container ${isExpanded ? 'expanded' : ''}`}>
                        <header className="pathway-header">
                            <h3>{pathway.name}</h3>
                            <p>{pathway.description}</p>

                            <button
                                className={`pathway-tagline-btn ${isExpanded ? 'active' : ''}`}
                                onClick={() => handleToggleExpand(pathway.id)}
                            >
                                {isExpanded ? (
                                    <> Close Details</>
                                ) : (
                                    <>{pathway.tagline} <span className="info-icon"></span></>
                                )}
                            </button>
                        </header>

                        {isExpanded ? (
                            <div className="pathway-inline-details fade-in">
                                {/* 1. Text Overview */}
                                <div className="details-text-content">
                                    <h4>Program Overview</h4>
                                    {pathway.fullDescription ? (
                                        pathway.fullDescription.map((paragraph, i) => (
                                            <p key={i}>{paragraph}</p>
                                        ))
                                    ) : (
                                        <p>{pathway.description}</p>
                                    )}
                                </div>

                                {/* 2. Pricing Section (Styled like CourseDetailsSection) */}
                                {pathway.pricing && (
                                    <div className="pathway-pricing-container">
                                        <h4 className="pricing-title">Pricing & Inclusions</h4>

                                        {/* Value Summary Bar */}
                                        {pathway.valueSummary && (
                                            <div className="value-summary-container">
                                                <div className="value-prop-item">
                                                    <span className="value-icon icon-calendar" aria-hidden="true"></span>
                                                    <span className="value-label">Duration</span>
                                                    <span className="value-detail">{pathway.valueSummary.duration}</span>
                                                </div>
                                                <div className="value-prop-item">
                                                    <span className="value-icon icon-screen" aria-hidden="true"></span>
                                                    <span className="value-label">Training</span>
                                                    <span className="value-detail">{pathway.valueSummary.sessions}</span>
                                                </div>
                                                <div className="value-prop-item">
                                                    <span className="value-icon icon-book" aria-hidden="true"></span>
                                                    <span className="value-label">Resources</span>
                                                    <span className="value-detail">{pathway.valueSummary.resources}</span>
                                                </div>
                                                <div className="value-prop-item">
                                                    <span className="value-icon icon-award" aria-hidden="true"></span>
                                                    <span className="value-label">Outcome</span>
                                                    <span className="value-detail">{pathway.valueSummary.outcome}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="pathway-pricing-grid">
                                            {pathway.pricing.map((plan, index) => (
                                                <div key={index} className="pathway-pricing-card">
                                                    <h3 className="pricing-card-name">{plan.label}</h3>
                                                    <p className="pricing-card-amount">{plan.amount}</p>
                                                    <p className="pricing-card-mode"><em>{plan.per}</em></p>

                                                    <ul className="pricing-card-features">
                                                        {plan.features ? (
                                                            plan.features.map((feature, fIndex) => (
                                                                <li key={fIndex}>{feature}</li>
                                                            ))
                                                        ) : (
                                                            <>
                                                                <li>Full access to all modules</li>
                                                                <li>Lifetime resource access</li>
                                                            </>
                                                        )}
                                                    </ul>

                                                    <button className="pricing-enroll-btn" onClick={() => handleEnrollNowClick(pathway.name)}>Enroll Now</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // 3. The Horizontal Scroll View (Collapsed State)
                            <PathwayScrollWrapper>
                                {pathway.modules.map((module, index) => (
                                    <PathwayBlock
                                        key={index}
                                        title={module.title}
                                        details={module.details}
                                        workshops={module.workshops}
                                    />
                                ))}
                            </PathwayScrollWrapper>
                        )}
                    </div>
                );
            })}
        </section>
    );
}

// --- Internal Helper Component for Horizontal Scroll ---
const PathwayScrollWrapper = ({ children }) => {
    const scrollRef = useRef(null);
    const scrollInterval = useRef(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(true);

    const checkScroll = () => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setShowLeft(scrollLeft > 0);
        setShowRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 20);
    };

    useEffect(() => {
        const ref = scrollRef.current;
        if (ref) {
            ref.addEventListener('scroll', checkScroll);
            checkScroll();
            window.addEventListener('resize', checkScroll);
        }
        return () => {
            if (ref) ref.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
            stopScrolling();
        };
    }, []);

    const startScrolling = (direction) => {
        if (scrollInterval.current) return;
        const speed = 15;
        scrollInterval.current = setInterval(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollBy({
                    left: direction === 'left' ? -speed : speed,
                    behavior: 'auto'
                });
            }
        }, 16);
    };

    const stopScrolling = () => {
        if (scrollInterval.current) {
            clearInterval(scrollInterval.current);
            scrollInterval.current = null;
        }
    };

    return (
        <div className="pathway-scroll-wrapper fade-in">
            <div
                className={`scroll-arrow-zone left-zone ${showLeft ? 'visible' : ''}`}
                onMouseEnter={() => startScrolling('left')}
                onMouseLeave={stopScrolling}
            >
                <button className="scroll-arrow" aria-label="Scroll left">&#8249;</button>
            </div>

            <div className="pathway-blocks-container" ref={scrollRef}>
                {children}
            </div>

            <div
                className={`scroll-arrow-zone right-zone ${showRight ? 'visible' : ''}`}
                onMouseEnter={() => startScrolling('right')}
                onMouseLeave={stopScrolling}
            >
                <button className="scroll-arrow" aria-label="Scroll right">&#8250;</button>
            </div>
        </div>
    );
};

export default PathwaysSection;